import { NavLink, Outlet, ScrollRestoration, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { ROUTES } from "@/app/routes";
import { Button } from "@/components/ui/Button";
import { CyberPanel } from "@/components/ui/CyberPanel";
import { useCurrentUser, useLogout } from "@/features/auth/hooks";
import { roleAtLeast } from "@/features/auth/roles";
import type { Role } from "@/features/auth/types";
import { cn } from "@/lib/cn";

const adminLinks = [
  { label: "Dashboard", to: ROUTES.admin.root, end: true, minimumRole: "EDITOR" },
  { label: "About", to: ROUTES.admin.about, minimumRole: "EDITOR" },
  { label: "Portfolio", to: ROUTES.admin.portfolio, minimumRole: "EDITOR" },
  { label: "Categories", to: ROUTES.admin.portfolioCategories, minimumRole: "EDITOR" },
  { label: "Partners", to: ROUTES.admin.partners, minimumRole: "EDITOR" },
  { label: "References", to: ROUTES.admin.references, minimumRole: "EDITOR" },
  { label: "Commissions", to: ROUTES.admin.commissions, minimumRole: "EDITOR" },
  { label: "Contact", to: ROUTES.admin.contactSubmissions, minimumRole: "ADMIN" },
  { label: "Legal", to: ROUTES.admin.legal, minimumRole: "EDITOR" },
  { label: "Media", to: ROUTES.admin.media, minimumRole: "EDITOR" },
  { label: "Socials", to: ROUTES.admin.socials, minimumRole: "EDITOR" },
  { label: "Settings", to: ROUTES.admin.settings, minimumRole: "ADMIN" },
  { label: "Users", to: ROUTES.admin.users, minimumRole: "ADMIN" },
  { label: "Developer", to: ROUTES.admin.settings, minimumRole: "DEVELOPER" },
] satisfies Array<{ label: string; to: string; end?: boolean; minimumRole: Role }>;

export function AdminLayout() {
  const currentUser = useCurrentUser();
  const logout = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  const user = currentUser.data;
  const [menuOpen, setMenuOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const visibleLinks = adminLinks.filter((link) => roleAtLeast(user?.role, link.minimumRole));
  const currentSection =
    [...visibleLinks].reverse().find((link) => (link.end ? location.pathname === link.to : location.pathname.startsWith(link.to)))
      ?.label ?? "Admin";

  function handleLogout() {
    logout.mutate(undefined, {
      onSettled: () => navigate(ROUTES.admin.login, { replace: true }),
    });
  }

  return (
    <div className="min-h-screen bg-ink text-content-primary">
      <div className="cyber-grid fixed inset-0 opacity-40" />
      <div className="relative grid min-h-screen lg:grid-cols-[18rem_1fr]">
        <aside className="border-b border-cyan-400/20 bg-surface/85 p-4 backdrop-blur lg:border-b-0 lg:border-r">
          <div className="mb-6 flex items-center justify-between gap-4">
            <NavLink to={ROUTES.home} className="font-display text-xl font-bold uppercase tracking-[0.18em] text-white">
              Kalaco
            </NavLink>
            <div className="flex items-center gap-2">
              <span className="rounded border border-teal-300/40 px-2 py-1 text-xs uppercase text-teal-200">Admin</span>
              <button
                type="button"
                className="rounded border border-cyan-300/25 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-100 lg:hidden"
                aria-controls="admin-navigation"
                aria-expanded={navOpen}
                onClick={() => setNavOpen((current) => !current)}
              >
                Menu
              </button>
            </div>
          </div>
          <nav
            id="admin-navigation"
            className={cn(
              "gap-2 overflow-x-auto lg:flex lg:flex-col lg:overflow-visible",
              navOpen ? "flex" : "hidden",
            )}
          >
            {visibleLinks.map((link) => (
              <NavLink
                key={`${link.label}-${link.to}`}
                to={link.to}
                onClick={() => setNavOpen(false)}
                end={link.end}
                className={({ isActive }) =>
                  cn(
                    "whitespace-nowrap rounded-md border px-3 py-2 text-sm font-medium transition",
                    isActive
                      ? "border-cyan-300/70 bg-cyan-300/10 text-white shadow-neon"
                      : "border-transparent text-content-secondary hover:border-teal-300/30 hover:bg-white/5 hover:text-white",
                  )
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>
        </aside>
        <main className="min-w-0 p-4 sm:p-6 lg:p-8">
          <div className="mb-5 flex flex-col gap-3 border-b border-cyan-300/15 pb-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-teal-200">Current Section</p>
              <h1 className="mt-1 font-display text-2xl font-bold uppercase text-white">{currentSection}</h1>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <button
                  type="button"
                  className="flex items-center gap-3 rounded-md border border-cyan-300/20 bg-black/25 px-3 py-2 text-left transition hover:border-cyan-300/50"
                  onClick={() => setMenuOpen((current) => !current)}
                >
                  <span className="grid size-9 place-items-center rounded-full border border-cyan-300/40 bg-cyan-300/10 font-display text-sm text-cyan-100">
                    {user?.displayName?.slice(0, 1) ?? "U"}
                  </span>
                  <span className="hidden sm:block">
                    <span className="block text-sm font-medium text-white">{user?.displayName}</span>
                    <span className="block text-xs uppercase tracking-[0.18em] text-content-muted">{user?.role}</span>
                  </span>
                </button>
                {menuOpen && (
                  <div className="absolute right-0 z-40 mt-2 w-56 rounded-md border border-cyan-300/20 bg-surface p-2 shadow-panel">
                    <NavLink
                      to={ROUTES.admin.profile}
                      className="block rounded px-3 py-2 text-sm text-content-secondary hover:bg-white/5 hover:text-white"
                      onClick={() => setMenuOpen(false)}
                    >
                      Profile
                    </NavLink>
                    <NavLink
                      to={`${ROUTES.admin.profile}?tab=password`}
                      className="block rounded px-3 py-2 text-sm text-content-secondary hover:bg-white/5 hover:text-white"
                      onClick={() => setMenuOpen(false)}
                    >
                      Change Password
                    </NavLink>
                    <Button className="mt-1 w-full justify-start" size="sm" variant="ghost" onClick={handleLogout} disabled={logout.isPending}>
                      Logout
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <CyberPanel className="min-h-[calc(100vh-8rem)]">
            <Outlet />
          </CyberPanel>
        </main>
      </div>
      <ScrollRestoration />
    </div>
  );
}
